import React, { useEffect, useState } from 'react'
import { Box, Collapse, InputAdornment } from '@mui/material'
import { withStyles } from '@mui/styles'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'

import { FilterURL } from '../filter-url'
import { TextInput } from '../text-input'
import { ThemeButton } from '../theme-button'

import { filterStyles } from './filter-box.styles'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTypingRefresh } from '../../core/utils/useTypingRefresh'

const DEFAULT_TYPE_REFRESH_MS = 800

/**
 * @typedef {object} FilterConfig
 * @property {string} name - El nombre del parámetro de filtro en la URL.
 * @property {string} label - La etiqueta visible del filtro.
 * @property {'input' | 'autocomplete' | 'date' | 'switch' | 'basic-input'} [filterType='input'] - El tipo de componente de filtro a renderizar.
 * @property {any} [default] - El valor por defecto del filtro.
 * @property {string[]} [cleanup] - Un array de nombres de otros filtros que deben limpiarse cuando este filtro cambia.
 */

/**
 * @callback ChoiceFunction
 * @param {FilterConfig} filter - La configuración del filtro.
 * @returns {React.ReactElement} El componente de filtro renderizado.
 */

/**
 * @typedef {object} FilterBoxProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {FilterConfig[]} [filters=[]] - Un array de objetos de configuración que definen los filtros disponibles.
 * @property {ChoiceFunction} [choice] - Una función personalizada para renderizar cada filtro. Si no se proporciona, se usa `FilterURL` por defecto.
 * @property {function(): void} [cleanup] - Una función personalizada para limpiar todos los filtros. Si no se proporciona, se limpia la URL excepto el parámetro `_num_x_pagina`.
 * @property {string} [searchPlaceholder='Buscar'] - El texto de marcador de posición para el campo de búsqueda principal.
 * @property {string} [cleanupFiltersLabel='Limpiar filtros'] - El texto del botón para limpiar todos los filtros.
 * @property {string} [toggleFiltersLabel='Filtros'] - El texto del botón para mostrar/ocultar el panel de filtros.
 * @property {number} [typeRefresh=800] - El tiempo de espera en milisegundos (debounce) antes de actualizar la URL después de que el usuario deja de escribir en el campo de búsqueda.
 * @property {React.ReactNode} [children] - Elementos adicionales a renderizar a la derecha del campo de búsqueda y los botones de filtro.
 */

/**
 * Un componente completo de panel de filtros que incluye un campo de búsqueda principal
 * y un conjunto de filtros colapsables.
 * 
 * Gestiona automáticamente la sincronización de todos los filtros con los parámetros de la URL,
 * implementa debounce en el campo de búsqueda, y proporciona botones para limpiar filtros
 * y alternar la visibilidad del panel de filtros avanzados.
 * 
 * **Nota:** Este componente depende de `react-router-dom` para la navegación y gestión de URL.
 * 
 * @component
 * @example
 * const MyDataTable = () => {
 *   const filters = [
 *     { name: 'status', label: 'Estado', filterType: 'autocomplete', values: [...] },
 *     { name: 'createdFrom', label: 'Desde', filterType: 'date' },
 *     { name: 'isActive', label: 'Solo activos', filterType: 'switch' },
 *   ];
 * 
 *   return (
 *     <FilterBox
 *       filters={filters}
 *       searchPlaceholder="Buscar por nombre o ID..."
 *       cleanupFiltersLabel="Limpiar"
 *       toggleFiltersLabel="Más filtros"
 *     >
 *       <ThemeButton onClick={handleExport}>Exportar</ThemeButton>
 *     </FilterBox>
 *   );
 * };
 * 
 * @param {FilterBoxProps} props - Las props del componente FilterBox.
 */
export const FilterBox = withStyles(filterStyles)(({
  classes,
  filters = [],
  choice,
  cleanup,
  searchPlaceholder = 'Buscar',
  cleanupFiltersLabel = 'Limpiar filtros',
  toggleFiltersLabel = 'Filtros',
  typeRefresh = DEFAULT_TYPE_REFRESH_MS,
  children,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const currentSearch = new URLSearchParams(location.search)

  const searchQuery = currentSearch.get('_search')

  const filterChoice = choice || ((filter) => <FilterURL {...filter} />)
  const filterCleanup =
    cleanup ||
    (() => {
      const pageSize = currentSearch.get('_num_x_pagina')
      navigate({ search: pageSize ? '?_num_x_pagina=' + pageSize : '' })
    })

  const [formState, setFormState] = useState({ _search: searchQuery })
  useEffect(() => setFormState({ _search: searchQuery }), [searchQuery])
  const handleChangeForm = (event) => {
    setFormState({ ...formState, [event.target.name]: event.target.value })
  }

  const handleSubmitForm = (event) => {
    event.preventDefault()
    if (isSearchChanged(event.target.value)) {
      changeSearchValue(event.target.value)
    }
  }

  const isSearchChanged = (value) => {
    let searchChanged = false
    if (value && searchQuery) {
      searchChanged = !(value === searchQuery)
    }
    return searchChanged
  }

  const changeSearchValue = (search) => {
    if (!((!search || search.length === 0) && !currentSearch.get('_search'))) {
      currentSearch.set('_search', search)
      currentSearch.set('_pagina', 1)
      navigate({
        search: '?' + currentSearch.toString(),
      })
    }
  }

  const { ref: textRef } = useTypingRefresh({
    timeout: typeRefresh,
    action: (val) => changeSearchValue(val),
    textFromRef: (ref) => (ref.current ? ref.current.querySelector('input') : null),
  })

  const [filtersOpen, setFiltersOpen] = useState(false)
  const handleToggleFilters = () => {
    setFiltersOpen(!filtersOpen)
  }

  return (
    <Box width="100%">
      <Box display="flex" flexWrap="wrap">
        <Box flexGrow={1} justifyContent="flex-start">
          <Box display="flex">
            <Box style={{ flex: 1 }} ref={typeRefresh ? textRef : null}>
              <TextInput
                spaceless="true"
                name="_search"
                placeholder={searchPlaceholder}
                value={formState['_search']}
                onChange={handleChangeForm}
                onBlur={handleSubmitForm}
                onKeyDown={(e) => e.keyCode === 13 && handleSubmitForm(e)}
                className={classes.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box ml={filters.length ? 2 : 0} display="flex">
              {filters.length ? (
                <ThemeButton color="primary" onClick={filterCleanup}>
                  {cleanupFiltersLabel}
                </ThemeButton>
              ) : null}
              {filters.length ? (
                <ThemeButton
                  variant="outlined"
                  endIcon={<FilterListIcon />}
                  onClick={handleToggleFilters}
                  style={{ marginLeft: '1em' }}
                >
                  {toggleFiltersLabel}
                </ThemeButton>
              ) : null}
            </Box>
          </Box>
        </Box>
        {!!children ? (
          <Box ml={2} display="flex">
            {children}
          </Box>
        ) : null}
      </Box>
      <Box>
        <Collapse in={filtersOpen}>
          <Box display="flex" flexWrap="wrap" pt={2}>
            {filters.map((filter, i) => {
              return (
                <Box key={i} className={classes.filterSpace}>
                  {filterChoice(filter)}
                </Box>
              )
            })}
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
})

export default FilterBox
